import React, { useEffect, useState } from "react";
import { X } from "lucide-react";

type Props = {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    count: number;
    onClear: () => void;
    children: React.ReactNode;
};

export function FilterDrawer({ isOpen, onClose, title, count, onClear, children }: Props) {
    const [shouldRender, setShouldRender] = useState(isOpen);
    const [isAnimating, setIsAnimating] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setShouldRender(true);
            // Small delay to ensure the component is in the DOM before animating
            const timer = setTimeout(() => setIsAnimating(true), 10);
            return () => clearTimeout(timer);
        } else {
            setIsAnimating(false);
            const timer = setTimeout(() => setShouldRender(false), 300);
            return () => clearTimeout(timer);
        }
    }, [isOpen]);

    // Prevent body scroll when drawer is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
        return () => {
            document.body.style.overflow = "";
        };
    }, [isOpen]);

    if (!shouldRender) return null;

    return (
        <div className={`fixed inset-0 z-[60] flex flex-col sm:hidden transition-all duration-300 ${isAnimating ? 'pointer-events-auto' : 'pointer-events-none'}`}>
            {/* Backdrop */}
            <div
                className={`absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${isAnimating ? 'opacity-100' : 'opacity-0'}`}
                onClick={onClose}
            />

            {/* Content */}
            <div
                className="relative mt-auto flex max-h-[90vh] flex-col rounded-t-[32px] bg-white shadow-2xl transition-transform duration-300 ease-out"
                style={{ transform: isAnimating ? 'translateY(0)' : 'translateY(100%)' }}
            >
                {/* Handle */}
                <div className="flex justify-center p-3">
                    <div className="h-1.5 w-12 rounded-full bg-muted" />
                </div>

                {/* Header */}
                <div className="flex items-center justify-between px-6 py-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-muted"
                    >
                        <X className="h-6 w-6" />
                    </button>
                    <h2 className="text-xl font-bold text-primary">{title}</h2>
                    <button
                        type="button"
                        onClick={onClear}
                        className="text-sm font-semibold text-accent hover:underline"
                    >
                        Nullstill
                    </button>
                </div>

                {/* Filters Body */}
                <div className="flex-1 overflow-y-auto px-6 py-4">
                    <div className="space-y-8">
                        {children}
                    </div>
                </div>

                {/* Footer */}
                <div className="border-t border-border p-6 pb-8 bg-white/80 backdrop-blur-md">
                    <div className="flex gap-3">
                        <button
                            type="button"
                            className="flex-1 rounded-xl border border-border py-4 text-sm font-bold text-primary hover:bg-muted active:scale-95 transition-all"
                        >
                            Lagre søket
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-[2] rounded-xl bg-primary py-4 text-sm font-bold text-white shadow-lg active:scale-95 transition-all"
                        >
                            Vis {count} treff
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
